/**
 * Speurtocht Progress Screen
 * Monitor team progress and verify submissions
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  useSpeurttochtAdmin,
  Speurtocht,
  SpeurttochtSession,
  SpeurttochtStepCompletion,
  SpeurttochtStep,
  STEP_TYPE_CONFIG,
  STEP_TYPE_COLORS,
} from "@/modules/speurtocht";

/**
 * Format time duration
 */
const formatDuration = (startTime: string, endTime?: string | null) => {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const diffMs = end.getTime() - start.getTime();

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}u ${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
};

/**
 * Session status badge
 */
const getSessionStatus = (session: SpeurttochtSession) => {
  switch (session.status) {
    case "completed":
      return { label: "Voltooid", color: "#10B981", icon: "checkmark-circle" };
    case "active":
      return { label: "Bezig", color: "#3B82F6", icon: "play-circle" };
    case "expired":
      return { label: "Verlopen", color: "#EF4444", icon: "time" };
    case "abandoned":
      return { label: "Gestopt", color: "#6B7280", icon: "close-circle" };
    default:
      return { label: "Onbekend", color: "#6B7280", icon: "help-circle" };
  }
};

/**
 * Submission data display component
 */
interface SubmissionDataViewProps {
  submission: Record<string, unknown>;
  colors: ReturnType<typeof useThemeColors>;
}

const SubmissionDataView = ({ submission, colors }: SubmissionDataViewProps) => {
  const submissionData = submission as Record<string, unknown>;
  const photoUrls = submissionData?.photo_urls;
  const answer = submissionData?.answer;
  const distanceMeters = submissionData?.distance_meters;

  return (
    <>
      {/* Photo submission */}
      {photoUrls && Array.isArray(photoUrls) && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="mb-2"
        >
          {(photoUrls as string[]).map((url: string, i: number) => (
            <Image
              key={i}
              source={{ uri: url }}
              className="w-16 h-16 rounded-lg mr-2"
              resizeMode="cover"
            />
          ))}
        </ScrollView>
      )}

      {/* Text submission */}
      {answer && typeof answer === "string" && (
        <View
          className="px-2 py-1 rounded"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          <Text className="text-sm" style={{ color: colors.text }}>
            "{answer}"
          </Text>
        </View>
      )}

      {/* Location submission */}
      {distanceMeters && typeof distanceMeters === "number" && (
        <Text className="text-xs" style={{ color: colors.textSecondary }}>
          Afstand: {Math.round(distanceMeters)}m
        </Text>
      )}
    </>
  );
};

export default function ProgressScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const {
    fetchSpeurtocht,
    fetchSteps,
    fetchSessions,
    fetchCompletions,
    verifyCompletion,
    isSubmitting,
  } = useSpeurttochtAdmin();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [speurtocht, setSpeurtocht] = useState<Speurtocht | null>(null);
  const [steps, setSteps] = useState<SpeurttochtStep[]>([]);
  const [sessions, setSessions] = useState<SpeurttochtSession[]>([]);
  const [completions, setCompletions] = useState<Record<string, SpeurttochtStepCompletion[]>>({});
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  /**
   * Load data
   */
  const loadData = useCallback(async () => {
    if (!id) return;

    const [speurttochtData, stepsData, sessionsData] = await Promise.all([
      fetchSpeurtocht(id),
      fetchSteps(id),
      fetchSessions(id),
    ]);

    setSpeurtocht(speurttochtData);
    setSteps(stepsData);
    setSessions(sessionsData);

    // Load completions for each session
    const completionsMap: Record<string, SpeurttochtStepCompletion[]> = {};
    await Promise.all(
      sessionsData.map(async (session) => {
        const sessionCompletions = await fetchCompletions(session.id);
        completionsMap[session.id] = sessionCompletions;
      })
    );
    setCompletions(completionsMap);

    setIsLoading(false);
  }, [id, fetchSpeurtocht, fetchSteps, fetchSessions, fetchCompletions]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  }, [loadData]);

  /**
   * Handle verify
   */
  const handleVerify = async (
    completion: SpeurttochtStepCompletion,
    approved: boolean
  ) => {
    const action = approved ? "goedkeuren" : "afwijzen";
    Alert.alert(
      `Inzending ${action}`,
      `Weet je zeker dat je deze inzending wilt ${action}?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: approved ? "Goedkeuren" : "Afwijzen",
          style: approved ? "default" : "destructive",
          onPress: async () => {
            const success = await verifyCompletion(completion.id, approved);
            if (success) {
              // Update local state
              setCompletions((prev) => ({
                ...prev,
                [completion.session_id]: prev[completion.session_id].map((c) =>
                  c.id === completion.id
                    ? {
                        ...c,
                        is_verified: approved,
                        verified_at: new Date().toISOString(),
                      }
                    : c
                ),
              }));
            }
          },
        },
      ]
    );
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color="#10B981" />
      </SafeAreaView>
    );
  }

  if (!speurtocht) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Text style={{ color: colors.text }}>Speurtocht niet gevonden</Text>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mt-4 px-4 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface }}
        >
          <Text style={{ color: colors.primary }}>Terug</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // Calculate stats
  const totalTeams = sessions.length;
  const completedTeams = sessions.filter((s) => s.status === "completed").length;
  const inProgressTeams = sessions.filter((s) => s.status === "active").length;
  const expiredTeams = sessions.filter((s) => s.status === "expired").length;
  const pendingVerifications = Object.values(completions)
    .flat()
    .filter((c) => c.is_verified === null).length;

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center flex-1">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text
              className="text-xl font-bold"
              style={{ color: colors.text }}
              numberOfLines={1}
            >
              Voortgang
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {speurtocht.name}
            </Text>
          </View>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Stats Overview */}
        <View className="px-4 mb-4">
          <View
            className="rounded-xl p-4"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row justify-between">
              <View className="items-center flex-1">
                <Text className="text-2xl font-bold" style={{ color: colors.text }}>
                  {totalTeams}
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Teams
                </Text>
              </View>
              <View className="items-center flex-1">
                <Text className="text-2xl font-bold" style={{ color: "#10B981" }}>
                  {completedTeams}
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Voltooid
                </Text>
              </View>
              <View className="items-center flex-1">
                <Text className="text-2xl font-bold" style={{ color: "#3B82F6" }}>
                  {inProgressTeams}
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Bezig
                </Text>
              </View>
              <View className="items-center flex-1">
                <Text className="text-2xl font-bold" style={{ color: "#EF4444" }}>
                  {expiredTeams}
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Verlopen
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Pending Verifications Alert */}
        {pendingVerifications > 0 && (
          <View className="px-4 mb-4">
            <View
              className="rounded-xl p-3 flex-row items-center"
              style={{ backgroundColor: "#F59E0B20" }}
            >
              <Ionicons name="alert-circle" size={20} color="#F59E0B" />
              <Text className="ml-2 flex-1" style={{ color: "#F59E0B" }}>
                {pendingVerifications} inzending
                {pendingVerifications !== 1 ? "en" : ""} wacht
                {pendingVerifications === 1 ? "" : "en"} op verificatie
              </Text>
            </View>
          </View>
        )}

        {/* Sessions List */}
        {sessions.length === 0 ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="people" size={40} color="#10B981" />
            </View>
            <Text
              className="text-lg font-semibold mb-2"
              style={{ color: colors.text }}
            >
              Nog geen teams gestart
            </Text>
            <Text
              className="text-center"
              style={{ color: colors.textSecondary }}
            >
              Zodra teams beginnen zie je hier hun voortgang
            </Text>
          </View>
        ) : (
          <View className="px-4 pb-4">
            {sessions.map((session) => {
              const sessionStatus = getSessionStatus(session);
              const sessionCompletions = completions[session.id] || [];
              const completedSteps = sessionCompletions.filter(
                (c) => c.is_verified !== false
              ).length;
              const isExpanded = expandedSession === session.id;

              return (
                <View
                  key={session.id}
                  className="rounded-xl mb-3 overflow-hidden"
                  style={{
                    backgroundColor: colors.surface,
                    borderLeftWidth: 4,
                    borderLeftColor: sessionStatus.color,
                  }}
                >
                  {/* Session Header */}
                  <TouchableOpacity
                    onPress={() =>
                      setExpandedSession(isExpanded ? null : session.id)
                    }
                    activeOpacity={0.7}
                    className="p-4"
                  >
                    <View className="flex-row items-center justify-between">
                      <View className="flex-1">
                        <Text
                          className="font-bold text-base"
                          style={{ color: colors.text }}
                        >
                          {session.team?.name || "Team"}
                        </Text>
                        <View className="flex-row items-center mt-1">
                          <View
                            className="flex-row items-center px-2 py-0.5 rounded mr-2"
                            style={{ backgroundColor: `${sessionStatus.color}20` }}
                          >
                            <Ionicons
                              name={sessionStatus.icon as any}
                              size={12}
                              color={sessionStatus.color}
                            />
                            <Text
                              className="text-xs ml-1 font-medium"
                              style={{ color: sessionStatus.color }}
                            >
                              {sessionStatus.label}
                            </Text>
                          </View>
                          <Text
                            className="text-xs"
                            style={{ color: colors.textSecondary }}
                          >
                            {completedSteps}/{steps.length} stappen
                          </Text>
                        </View>
                      </View>

                      <View className="items-end">
                        <Text
                          className="font-bold"
                          style={{ color: colors.text }}
                        >
                          {session.total_points} pts
                        </Text>
                        <Text
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          {formatDuration(session.started_at, session.completed_at)}
                        </Text>
                      </View>

                      <Ionicons
                        name={isExpanded ? "chevron-up" : "chevron-down"}
                        size={20}
                        color={colors.textSecondary}
                        style={{ marginLeft: 8 }}
                      />
                    </View>

                    {/* Progress Bar */}
                    <View
                      className="h-2 rounded-full mt-3"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <View
                        className="h-2 rounded-full"
                        style={{
                          backgroundColor: sessionStatus.color,
                          width: `${(completedSteps / steps.length) * 100}%`,
                        }}
                      />
                    </View>
                  </TouchableOpacity>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <View
                      className="px-4 pb-4 pt-2"
                      style={{ borderTopWidth: 1, borderTopColor: colors.border }}
                    >
                      {steps.map((step, index) => {
                        const completion = sessionCompletions.find(
                          (c) => c.step_id === step.id
                        );
                        const stepConfig = STEP_TYPE_CONFIG[step.step_type];
                        const stepColor = STEP_TYPE_COLORS[step.step_type];

                        return (
                          <View
                            key={step.id}
                            className="py-3"
                            style={{
                              borderBottomWidth:
                                index < steps.length - 1 ? 1 : 0,
                              borderBottomColor: colors.border,
                            }}
                          >
                            <View className="flex-row items-start">
                              {/* Step Number & Status */}
                              <View
                                className="w-8 h-8 rounded-full items-center justify-center mr-3"
                                style={{
                                  backgroundColor: completion
                                    ? completion.is_verified === false
                                      ? `${colors.error}25`
                                      : completion.is_verified === true
                                      ? `${stepColor}25`
                                      : "#F59E0B25"
                                    : colors.surfaceSecondary,
                                }}
                              >
                                {completion ? (
                                  completion.is_verified === false ? (
                                    <Ionicons
                                      name="close"
                                      size={16}
                                      color={colors.error}
                                    />
                                  ) : completion.is_verified === true ? (
                                    <Ionicons
                                      name="checkmark"
                                      size={16}
                                      color={stepColor}
                                    />
                                  ) : (
                                    <Ionicons
                                      name="time"
                                      size={16}
                                      color="#F59E0B"
                                    />
                                  )
                                ) : (
                                  <Text
                                    className="text-xs font-bold"
                                    style={{ color: colors.textTertiary }}
                                  >
                                    {index + 1}
                                  </Text>
                                )}
                              </View>

                              {/* Step Info */}
                              <View className="flex-1">
                                <Text
                                  className="font-medium"
                                  style={{ color: colors.text }}
                                >
                                  {step.title}
                                </Text>
                                <View className="flex-row items-center mt-1">
                                  <Ionicons
                                    name={stepConfig.icon as any}
                                    size={12}
                                    color={stepColor}
                                  />
                                  <Text
                                    className="text-xs ml-1"
                                    style={{ color: colors.textSecondary }}
                                  >
                                    {stepConfig.label}
                                  </Text>
                                </View>

                                {/* Submission Data */}
                                {completion && completion.submission_data && (
                                  <SubmissionDataView 
                                    submission={completion.submission_data}
                                    colors={colors}
                                  />
                                )}

                                {/* Verification buttons */}
                                {completion && 
                                  completion.is_verified === null &&
                                  (speurtocht.require_photo_verification ||
                                    speurtocht.require_location_verification) && (
                                    <View className="flex-row mt-2">
                                      <TouchableOpacity
                                        onPress={() =>
                                          handleVerify(completion, true)
                                        }
                                        disabled={isSubmitting}
                                        className="px-3 py-1.5 rounded-lg mr-2 flex-row items-center"
                                        style={{
                                          backgroundColor: "#10B98120",
                                        }}
                                      >
                                        <Ionicons
                                          name="checkmark"
                                          size={14}
                                          color="#10B981"
                                        />
                                        <Text
                                          className="ml-1 text-sm font-medium"
                                          style={{ color: "#10B981" }}
                                        >
                                          Goedkeuren
                                        </Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        onPress={() =>
                                          handleVerify(completion, false)
                                        }
                                        disabled={isSubmitting}
                                        className="px-3 py-1.5 rounded-lg flex-row items-center"
                                        style={{
                                          backgroundColor: `${colors.error}20`,
                                        }}
                                      >
                                        <Ionicons
                                          name="close"
                                          size={14}
                                          color={colors.error}
                                        />
                                        <Text
                                          className="ml-1 text-sm font-medium"
                                          style={{ color: colors.error }}
                                        >
                                          Afwijzen
                                        </Text>
                                      </TouchableOpacity>
                                    </View>
                                  )}
                              </View>

                              {/* Points */}
                              {completion && completion.points_earned > 0 && (
                                <View
                                  className="px-2 py-1 rounded"
                                  style={{ backgroundColor: "#F59E0B20" }}
                                >
                                  <Text
                                    className="text-xs font-bold"
                                    style={{ color: "#F59E0B" }}
                                  >
                                    +{completion.points_earned}
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        <View className="h-12" />
      </ScrollView>
    </SafeAreaView>
  );
}

